import { useState, useEffect, useCallback } from 'react';
import {
  Settings as SettingsIcon, Link2, Store, ShoppingBag, Database, Wifi, WifiOff,
  CheckCircle, XCircle, Clock, RefreshCw, AlertTriangle, Plus
} from 'lucide-react';
import { api } from '../../../api/client';
import { StatusBadge } from '../../../components/shared/StatusBadge';
import OnboardingWizard from '../OnboardingWizard';
import { Can } from '../../../lib/rbac';
import { AmazonAssistantPanel } from './AmazonAssistantPanel';
import { MlBrowserPanel } from './MlBrowserPanel';

const SITE_FLAGS: Record<string, string> = { MLA: '\u{1F1E6}\u{1F1F7}', MLM: '\u{1F1F2}\u{1F1FD}', MLC: '\u{1F1E8}\u{1F1F1}', MCO: '\u{1F1E8}\u{1F1F4}', MLU: '\u{1F1FA}\u{1F1FE}', MLB: '\u{1F1E7}\u{1F1F7}' };

interface Seller {
  id: number;
  nickname: string;
  seller_ml_id: number;
  active: boolean;
  marketplace_user_ids: Record<string, number> | Array<{ site_id: string; user_id: number }> | null;
  has_token: boolean;
  token_expires: string | null;
  token_refreshed: string | null;
  active_listings: number;
  orders_24h: number;
}

interface CooldownStatus {
  active: boolean;
  reason: string | null;
  since: string | null;
  released_at: string | null;
  manual_hold: boolean;
  last_probe_at: string | null;
  last_probe_status: string | null;
  probe_count: number;
}

function MlCooldownPanel() {
  const [cd, setCd] = useState<CooldownStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get<CooldownStatus>('/admin/ml-cooldown');
    if (res.success && res.data) setCd(res.data as CooldownStatus);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const toggle = async (action: 'pause' | 'resume') => {
    setBusy(true);
    try {
      const res = await api.post<CooldownStatus>('/admin/ml-cooldown', { action });
      if (res.success && res.data) setCd(res.data as CooldownStatus);
    } finally {
      setBusy(false);
    }
  };

  const active = cd?.active ?? false;
  const estado = !active
    ? { label: 'Andando', color: 'var(--color-success, #16a34a)' }
    : cd?.released_at
      ? { label: 'Reactivando de a poco', color: '#d97706' }
      : cd?.reason === 'manual'
        ? { label: 'Pausa manual', color: '#d97706' }
        : { label: 'Enfriando (ML bloqueó)', color: '#dc2626' };

  return (
    <div className="rounded-lg border border-[var(--color-border)] p-4 bg-[var(--color-surface)]">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {active ? (
            <WifiOff className="w-4 h-4" style={{ color: estado.color }} />
          ) : (
            <Wifi className="w-4 h-4" style={{ color: estado.color }} />
          )}
          <div>
            <div className="text-sm font-medium text-[var(--color-text)]">Conexión Mercado Libre</div>
            <div className="text-xs" style={{ color: estado.color }}>
              {estado.label}
              {active && cd?.probe_count ? ` · ${cd.probe_count} chequeos` : ''}
            </div>
          </div>
        </div>
        {/* El estado se ve con `settings.connections`; frenar o reactivar exige la llave de freno. */}
        <Can cap="settings.connections_manage">
          {!active ? (
            <button
              disabled={busy}
              onClick={() => toggle('pause')}
              className="px-3 py-1.5 rounded-lg bg-[#dc2626] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
            >
              <AlertTriangle size={14} /> Pausa de emergencia
            </button>
          ) : (
            <button
              disabled={busy}
              onClick={() => toggle('resume')}
              className="px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
            >
              <RefreshCw size={14} /> Reactivar Mercado Libre
            </button>
          )}
        </Can>
      </div>
    </div>
  );
}

export function ConnectionsTab({ t }: { t: (k: string) => string }) {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get<{ sellers: Seller[] }>('/dashboard/connections');
    if (res.success && res.data) {
      setSellers((res.data as any).sellers || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-[var(--color-text-muted)]">
        <RefreshCw className="w-4 h-4 animate-spin" /> {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t('settings.connections')}</h2>
        <div className="flex items-center gap-2">
          <Can cap="settings.connections_manage">
            <button
              className="px-3 py-1.5 rounded-lg bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 flex items-center gap-1"
              onClick={() => setShowOnboarding(true)}
            >
              <Plus size={14} /> Agregar Seller
            </button>
          </Can>
          <button onClick={load} className="p-1.5 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <MlCooldownPanel />

      {/* Asistente de Amazon (Alexa for Shopping): sesión del navegador del servidor (2026-09-22) */}
      <AmazonAssistantPanel />

      {/* Navegador con la sesión web de Mercado Libre de cada empresa (2026-10-08) */}
      <MlBrowserPanel />

      {/* MercadoLibre Sellers */}
      <div>
        <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3 flex items-center gap-2">
          <Store className="w-4 h-4" /> MercadoLibre Sellers
        </h3>
        <div className="space-y-3">
          {sellers.map((seller) => {
            const tokenOk = seller.has_token && seller.token_expires && new Date(seller.token_expires) > new Date();
            const tokenExpiresIn = seller.token_expires
              ? Math.max(0, Math.floor((new Date(seller.token_expires).getTime() - Date.now()) / 3600000))
              : 0;

            // Parse marketplace_user_ids (can be {site: number} or {site: {user_id, logistic_type}})
            let childSites: Array<{ site_id: string; user_id: number; logistic_type?: string }> = [];
            if (seller.marketplace_user_ids) {
              if (Array.isArray(seller.marketplace_user_ids)) {
                childSites = seller.marketplace_user_ids;
              } else if (typeof seller.marketplace_user_ids === 'object') {
                childSites = Object.entries(seller.marketplace_user_ids).map(([site_id, val]) => {
                  if (typeof val === 'object' && val !== null && 'user_id' in (val as any)) {
                    return { site_id, user_id: (val as any).user_id, logistic_type: (val as any).logistic_type };
                  }
                  return { site_id, user_id: val as number };
                });
              }
            }

            return (
              <div key={seller.id} className="bg-[var(--color-bg)] rounded-lg border border-[var(--color-border)] p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    seller.active && tokenOk ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}>
                    {seller.active && tokenOk ? (
                      <Wifi className="w-5 h-5 text-green-400" />
                    ) : (
                      <WifiOff className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[var(--color-text)]">{seller.nickname}</span>
                      <StatusBadge
                        status={seller.active ? 'ok' : 'error'}
                        label={seller.active ? 'Activo' : 'Inactivo'}
                        size="sm"
                      />
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)] flex items-center gap-3 mt-1">
                      <span>ID: {seller.seller_ml_id}</span>
                      <span>Seller #{seller.id}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-[var(--color-text)]">{seller.active_listings} listings</p>
                    <p className="text-xs text-[var(--color-text-muted)]">{seller.orders_24h} ordenes/24h</p>
                  </div>
                </div>

                {/* Token status */}
                <div className="mt-3 pt-3 border-t border-[var(--color-border)] flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    {tokenOk ? (
                      <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-red-400" />
                    )}
                    <span className={tokenOk ? 'text-green-400' : 'text-red-400'}>
                      Token {tokenOk ? 'valido' : 'expirado'}
                    </span>
                  </div>
                  {tokenOk && (
                    <span className="text-[var(--color-text-muted)] flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Expira en {tokenExpiresIn}h
                    </span>
                  )}
                  {seller.token_refreshed && (
                    <span className="text-[var(--color-text-muted)]">
                      Refresh: {new Date(seller.token_refreshed).toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Child marketplace users */}
                {childSites.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                    <p className="text-xs text-[var(--color-text-muted)] mb-2">Marketplaces:</p>
                    <div className="flex flex-wrap gap-2">
                      {childSites.map(({ site_id, user_id }) => (
                        <span key={site_id} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[var(--color-surface-hover)] text-xs text-[var(--color-text)]">
                          {SITE_FLAGS[site_id] || ''} {site_id}
                          <span className="text-[var(--color-text-muted)]">({user_id})</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* External APIs section */}
      <div>
        <h3 className="text-sm font-medium text-[var(--color-text-secondary)] mb-3 flex items-center gap-2">
          <ShoppingBag className="w-4 h-4" /> APIs Externas
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { name: 'Amazon Business API', desc: 'Compras automatizadas', icon: ShoppingBag },
            { name: 'Keepa API', desc: 'Precios y disponibilidad', icon: Database },
            { name: 'KD100 Tracking', desc: 'Seguimiento de envios', icon: Store },
            { name: 'OpenAI / Claude', desc: 'Generacion de contenido AI', icon: SettingsIcon },
          ].map((apiItem) => (
            <div key={apiItem.name} className="flex items-center gap-3 p-3 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)]">
              <apiItem.icon className="w-5 h-5 text-[var(--color-text-muted)]" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[var(--color-text)]">{apiItem.name}</p>
                <p className="text-xs text-[var(--color-text-muted)]">{apiItem.desc}</p>
              </div>
              <StatusBadge status="info" label="Config V1" size="sm" />
            </div>
          ))}
        </div>
        <p className="text-xs text-[var(--color-text-muted)] mt-2 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          Las credenciales de APIs externas se configuran en la V1. Migracion pendiente.
        </p>
      </div>

      {showOnboarding && (
        <OnboardingWizard
          onClose={() => setShowOnboarding(false)}
          onComplete={() => {
            setShowOnboarding(false);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}
